import React from 'react';
import styled from 'styled-components';
import { RSSItem } from '../types/rss';

interface NewsCardProps {
  item: RSSItem;
  showImage: boolean;
  isHighlighted?: boolean;
}

const Card = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isHighlighted'
})<{ isHighlighted?: boolean }>`
  background-color: ${props => props.isHighlighted ? '#dc3545' : (props.theme === 'dark' ? 'var(--background-dark)' : 'white')};
  border-radius: 8px;
  box-shadow: var(--card-shadow);
  overflow: hidden;
  transition: var(--transition);
  height: 100%;
  display: flex;
  flex-direction: column;
  border: ${props => props.isHighlighted ? '3px solid #dc3545' : 'none'};
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);
  }
  
  body.dark-theme & {
    background-color: ${props => props.isHighlighted ? '#dc3545' : 'var(--background-dark)'};
    color: ${props => props.isHighlighted ? 'white' : 'var(--text-dark)'};
  }
`;

const CardImage = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'imageUrl'
})<{ imageUrl?: string }>`
  height: 180px;
  background-image: ${props => props.imageUrl ? `url(${props.imageUrl})` : 'none'};
  background-size: cover;
  background-position: center;
  background-color: ${props => !props.imageUrl ? 'var(--primary-color)' : 'transparent'};
`;

const CardContent = styled.div`
  padding: 16px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const CardTitle = styled.h3`
  font-size: 18px;
  margin-bottom: 8px;
  line-height: 1.3;
  
  body.dark-theme & {
    color: var(--text-dark);
  }
`;

const CardDescription = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 16px;
  flex-grow: 1;
  
  body.dark-theme & {
    color: #ccc;
  }
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #999;
  
  body.dark-theme & {
    color: #aaa;
  }
`;

const CardLink = styled.a`
  display: inline-block;
  color: var(--primary-color);
  font-weight: 500;
  
  &:hover {
    color: var(--secondary-color);
  }
`;

const formatDate = (dateString: string) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Função truncateText removida para mostrar texto completo

export const NewsCard: React.FC<NewsCardProps> = ({ item, showImage, isHighlighted = false }) => {
  return (
    <Card isHighlighted={isHighlighted}>
      {showImage && item.imageUrl && <CardImage imageUrl={item.imageUrl} />}
      <CardContent>
        <CardTitle>{item.title}</CardTitle>
        <CardDescription>{item.description}</CardDescription>
        <CardFooter>
          <span>{formatDate(item.pubDate)}</span>
          <CardLink href={item.link} target="_blank" rel="noopener noreferrer">
            Leia mais
          </CardLink>
        </CardFooter>
      </CardContent>
    </Card>
  );
};